import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffectOnValueChange, useMountEffect, usePrevious } from '@timelyapp/react';
import classNames from 'classnames';
import DisabledMask from 'components/DisabledMask';
import type { RefType as CustomScrollbarsRefType } from 'components/scrollbars';
import { Scrollbars } from 'components/scrollbars';
import type { Range } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import { ClickDragAdjust } from 'components/ui-blocks/click-drag-adjust/ClickDragAdjust';
import { TIMELINE_PX_OFFSET } from 'constants/memory';
import { TIMESPAN_SPACING_PX } from 'constants/timeline';
import { cloneMemoryWithTimespan } from 'factories/timeline';
import { useWithDispatch } from 'hooks/useWithDispatch';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toggleMemoryInbox } from 'redux/modules/calendar.actions';
import {
  createHourDraft,
  createHourDraftFromId,
  deleteHourDraft,
  deleteHourId,
  setHourDraftTimestamp,
  setHourTimestamp,
  toggleHourTimestampFromMemory,
} from 'redux/modules/hours.actions';
import type {
  deleteEntryIds,
  markEntryIdsAsNotSpam,
  markEntryIdsAsSpam,
  predictEntryIdsToProjectId,
} from 'redux/modules/timeline.actions';
import { setPixelsPerMinute as _setPixelsPerMinute } from 'redux/modules/timeline.actions';
import type { TimelineStyleTypes } from 'redux/modules/timeline.types';
import { setGutterHighlight } from 'redux/modules/timeline.types';
import { getAdjustableTimelineTimestamps } from 'redux/selectors/hours.timestamps.base.selector';
import type { MemorySidebarUIProps } from 'redux/selectors/timeline.memories';
import CurrentTimeIndicator from 'routes/supercalendar/components/calendar/memory/CurrentTimeIndicator';
import MemoryItemOnTimeline from 'routes/supercalendar/components/calendar/memory/MemoryItemOnTimeline';
import { MemoryTimelineEmptyState } from 'routes/supercalendar/components/calendar/memory/MemoryTimelineEmptyState';
import { MemoryTimelinePlaceholder } from 'routes/supercalendar/components/calendar/memory/MemoryTimelinePlaceholder';
import { TimelineZoomControl } from 'routes/supercalendar/components/calendar/memory/TimelineZoomControl';
import TimelyAnalytics from 'tracking/timely-analytics';
import type { MappedAction } from 'types/redux';
import type { ExtractTimelineMetaFromEntries, Memory } from 'types/timeline';
import { calculateDuration } from 'utils/durations';
import { datetimeTools } from 'utils/time';
import { getHourTimestampWidth, getTotalHourTimestampWidth } from 'utils/timeline';
import { generateStripedBgPattern } from '../Day.util.striped-background';
import styles from './MemoryTimeline.module.scss';
import MemoryTimelineSubscriptionPrompt from './MemoryTimelineSubscriptionPrompt';
import { TimelineGutter } from './TimelineGutter';

export interface MemoryTimelineProps {
  timeline: Array<Array<Memory>>;
  meta: ExtractTimelineMetaFromEntries;
  toggleSelectEntryId: (entryId: number) => void;
  ui: MemorySidebarUIProps;
  pixelsPerMinute: number;
  compact?: boolean;
  disabled?: boolean;
  markEntryIdsAsSpam: MappedAction<typeof markEntryIdsAsSpam>;
  markEntryIdsAsNotSpam: MappedAction<typeof markEntryIdsAsNotSpam>;
  predictEntryIdsToProjectId: MappedAction<typeof predictEntryIdsToProjectId>;
  deleteEntryIds: MappedAction<typeof deleteEntryIds>;
  inline?: boolean;
  timeFormat: 12 | 24;
  preserveScrollPosition: (position: number | null) => unknown;
  onScroll?: (px: number) => void;
  timelineStyle?: TimelineStyleTypes;
  snapToMinutes: number;
  offsetTopPx: number;
  useProjectColorOnMemories?: boolean;
  isSubscriptionExpiredOrTrialEnded?: boolean;
}

const MEMORY_WIDTH = 200;

interface ScrollbarsRefType extends CustomScrollbarsRefType {
  view?: HTMLDivElement;
}

export const MemoryTimeline: React.FC<MemoryTimelineProps> = ({
  compact = false,
  deleteEntryIds: _deleteEntryIds,
  disabled = false,
  isSubscriptionExpiredOrTrialEnded = false,
  inline,
  markEntryIdsAsNotSpam: _markEntryIdsAsNotSpam,
  markEntryIdsAsSpam: _markEntryIdsAsSpam,
  meta,
  offsetTopPx,
  onScroll,
  pixelsPerMinute,
  predictEntryIdsToProjectId,
  preserveScrollPosition,
  snapToMinutes,
  timeFormat,
  timeline = [],
  timelineStyle,
  toggleSelectEntryId,
  ui,
  useProjectColorOnMemories,
}) => {
  const timelineContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollbarsRef = useRef<ScrollbarsRefType | null>(null);
  const virtualScrollbarRef = useRef<ScrollbarsRefType | null>(null);
  const scrollPositionBeforeZoom = useRef<number | undefined>();

  const parentRef = useRef<HTMLDivElement | null>(null);
  const timespanRef = useRef<HTMLDivElement | null>(null);
  const { hourDraftExists, hourDraftId, adjustableRanges } = useSelector(
    getAdjustableTimelineTimestamps
  );

  const actions = useWithDispatch({
    createHourDraft,
    setHourTimestamp,
    setHourDraftTimestamp,
    createHourDraftFromId,
    toggleHourTimestampFromMemory,
    setGutterHighlight,
    toggleMemoryInbox,
    setPixelsPerMinute: _setPixelsPerMinute,
    deleteHourDraft,
    deleteHourId,
  });

  const startOfDay = meta.fromDateTime;
  const currentDate = startOfDay.toISODate(); // Timely always starts/ends the timeline on "current day"

  const hourTimestampWidthPx = useMemo(() => getHourTimestampWidth(timelineStyle), [timelineStyle]);

  const totalTimestampsWidth = useMemo(
    () => getTotalHourTimestampWidth(adjustableRanges, hourTimestampWidthPx),
    [adjustableRanges, hourTimestampWidthPx]
  );

  const totalTimelineWidth = timeline.length * MEMORY_WIDTH + totalTimestampsWidth;

  const [timelineWidthPx, setTimelineWidthPx] = useState<number | null>(null);

  useMountEffect(() => {
    window.addEventListener('resize', handleTimelineWidthSetup, { passive: true });

    // Detect and set timeline width. Also sets state, which forces a re-render
    // that also works around the issue we've seen with refs not initializing initially,
    // causing selections (ClickDragAdjust) to not work initially.
    handleTimelineWidthSetup();

    // If we're in the inline view (i.e. not the hour draft editor modal), try to restore the
    // previous scroll position (for example when closing the hour draft editor)
    const scrollPosition =
      inline && ui.preservedScrollPosition !== null
        ? ui.preservedScrollPosition
        : meta.scrollToPxFromTop;

    handleScrollToPx(scrollPosition);
    return () => {
      window.removeEventListener('resize', handleTimelineWidthSetup);
      if (inline) {
        preserveScrollPosition(scrollbarsRef.current?.view?.scrollTop ?? null);
      }
    };
  });

  /**
   * Some interactions will cause a store update which indirectly changes the `scrollToPxFromTop`,
   * but we don't want that to scroll the timeline.
   * */
  const ignoreNextScroll = useRef(false);

  const resetScroll = (behavior: 'auto' | 'smooth' = 'smooth') => {
    if (ignoreNextScroll.current) {
      ignoreNextScroll.current = false;
      return;
    }
    handleScrollToPx(meta.scrollToPxFromTop, behavior);
  };
  useEffectOnValueChange(meta.date, resetScroll);

  // When the content of the timeline changes, we need to re-calculate width for the
  // HourTimespans indicators & drag sizing to be correct
  useEffectOnValueChange(meta.totalMemories, () => handleTimelineWidthSetup());
  useEffectOnValueChange(timelineStyle, () => handleTimelineWidthSetup());

  const handleScrollToPx = useCallback(
    (px: number, behavior: 'auto' | 'smooth' = 'auto') => {
      scrollbarsRef.current?.view?.scroll?.({ top: px, behavior });
      onScroll?.(px);
    },
    [onScroll]
  );

  const handleTimelineWidthSetup = useCallback(() => {
    if (timelineContainerRef.current === null) {
      return;
    }
    const newWidthPx = timelineContainerRef.current.scrollWidth || null;

    if (newWidthPx !== timelineWidthPx) {
      setTimelineWidthPx(newWidthPx);
    }
  }, [timelineWidthPx]);

  const markEntryIdsAsSpam: typeof _markEntryIdsAsSpam = useCallback(
    entryIds => {
      ignoreNextScroll.current = true;
      return _markEntryIdsAsSpam(entryIds);
    },
    [_markEntryIdsAsSpam]
  );

  const markEntryIdsAsNotSpam: typeof _markEntryIdsAsNotSpam = useCallback(
    entryIds => {
      ignoreNextScroll.current = true;
      return _markEntryIdsAsNotSpam(entryIds);
    },
    [_markEntryIdsAsNotSpam]
  );

  const deleteEntryIds: typeof _deleteEntryIds = useCallback(
    entryIds => {
      ignoreNextScroll.current = true;
      return _deleteEntryIds(entryIds);
    },
    [_deleteEntryIds]
  );

  const setPixelsPerMinute: typeof actions.setPixelsPerMinute = useCallback(
    pixelsPerMinute => {
      if (scrollbarsRef.current) {
        scrollPositionBeforeZoom.current = scrollbarsRef.current.getScrollTop();
        ignoreNextScroll.current = true;
      }
      actions.setPixelsPerMinute(pixelsPerMinute);
    },
    [actions]
  );

  const previousPixelsPerMinute = usePrevious(pixelsPerMinute);
  useEffectOnValueChange(pixelsPerMinute, () => {
    if (!scrollbarsRef.current || scrollPositionBeforeZoom.current == null) return;

    const previousTop = scrollPositionBeforeZoom.current;

    // This calculates the center of the visible view relative to the total scrollable container
    // height, which we can subtract or add to the new scroll position to zoom around the "middle"
    const focalPoint =
      scrollbarsRef.current.getClientHeight() ** 2 / scrollbarsRef.current.getScrollHeight() / 2;

    const scale = pixelsPerMinute / previousPixelsPerMinute;
    const zoomDirection = scale > 1 ? 1 : -1;
    const newTop = (previousTop + focalPoint * zoomDirection) * scale;

    handleScrollToPx(newTop, 'auto');
  });

  const renderMemories = (memories: Array<Memory>) => {
    if (!memories.length) {
      return null;
    }

    return memories.map(memory => {
      const memoryWithTimespan = cloneMemoryWithTimespan(memory, {
        color: memory.barColor,
        isChecked: !!memory.isChecked,
        startOfTimeline: meta.fromDateTime,
        endOfTimeline: meta.toDateTime,
        totalHours: meta.totalHours,
        pixelsPerMinute,
      });

      return (
        <MemoryItemOnTimeline
          toggleTimestampFromMemory={actions.toggleHourTimestampFromMemory}
          showDebugInfo={ui.showDebugInfo}
          key={[memoryWithTimespan.cacheBuster, ui.hideShortDurationMemoryTitles].join()}
          toggleSelectEntryId={toggleSelectEntryId}
          markEntryIdsAsSpam={markEntryIdsAsSpam}
          markEntryIdsAsNotSpam={markEntryIdsAsNotSpam}
          deleteEntryIds={deleteEntryIds}
          predictEntryIdsToProjectId={predictEntryIdsToProjectId}
          memory={memoryWithTimespan}
          useProjectColorOnMemories={useProjectColorOnMemories}
          hideShortDurationMemoryTitles={ui.hideShortDurationMemoryTitles}
        />
      );
    });
  };

  const handleTimestampChange = useCallback(
    ({ index, hourId, from, to }: Range) => {
      const shouldUpdateExistingHour = !!hourId && hourDraftId !== hourId;

      if (shouldUpdateExistingHour && index != null) {
        actions.setHourTimestamp(
          hourId,
          index,
          { from, to },
          { interaction: 'Timestamp Selection' }
        );
        return;
      }

      if (hourDraftExists) {
        const indexToChange = index;

        actions.setHourDraftTimestamp(indexToChange, {
          from,
          to,
        });
      } else {
        actions.createHourDraft(
          { interaction: 'Timestamp Selection' },
          {
            from,
            day: startOfDay.toISODate(),
            duration: calculateDuration(datetimeTools.secondsBetween(from, to)),
          }
        );

        actions.toggleMemoryInbox();
      }

      TimelyAnalytics.track(
        'Drag on Timeline',
        { action: hourDraftExists ? 'edit' : 'create' },
        {
          integrations: { Intercom: false },
        }
      );
    },
    [actions, hourDraftExists, hourDraftId, startOfDay]
  );

  const handleTimestampClick = useCallback(
    (hourId: number) => {
      actions.createHourDraftFromId(hourId, { interaction: 'Click Timestamp' });
    },
    [actions]
  );

  const handleTimestampDelete = useCallback(
    ({ index, hourId }: Range) => {
      const isLastTimestamp = (numTimestamps: number) => numTimestamps <= 1;
      // Handle draft timestamps
      if (hourDraftExists && !hourId) {
        const draftTimestamps = adjustableRanges.filter(range => !range.hourId);
        if (isLastTimestamp(draftTimestamps.length)) {
          return actions.deleteHourDraft();
        }
        return actions.setHourDraftTimestamp(index, null);
      }

      // Handle existing timestamps
      if (!hourId || index == null) return;

      const hourTimestamps = adjustableRanges.filter(range => range.hourId === hourId);
      if (isLastTimestamp(hourTimestamps.length)) {
        return hourDraftExists ? actions.deleteHourDraft() : actions.deleteHourId(hourId);
      }
      actions.setHourTimestamp(hourId, index, null, {
        interaction: 'Timestamp Selection',
      });
      if (hourDraftExists) {
        actions.setHourDraftTimestamp(index, null);
      }
    },
    [actions, adjustableRanges, hourDraftExists]
  );

  const handleTimestampDragEnd = useCallback(() => {
    actions.setGutterHighlight(null);
  }, [actions]);

  const renderHourTimespans = () => {
    if (!timelineContainerRef.current) {
      return null;
    }

    return (
      <ClickDragAdjust
        snapToMinutes={snapToMinutes}
        timeFormat={timeFormat}
        showFromToLabels
        selectionWidthPx={timelineWidthPx || 0}
        adjustWidthPx={hourTimestampWidthPx}
        adjustMarginPx={TIMESPAN_SPACING_PX}
        timelineStyle={timelineStyle}
        offsetLeftPx={10}
        indicatorWidthPx={totalTimelineWidth}
        adjustableRanges={adjustableRanges}
        startOfTimeline={startOfDay}
        currentDate={currentDate}
        offsetTopPx={offsetTopPx}
        pixelsPerMinute={pixelsPerMinute}
        onClick={handleTimestampClick}
        onChange={handleTimestampChange}
        onDelete={handleTimestampDelete}
        onDragEnd={handleTimestampDragEnd}
      />
    );
  };

  const renderTimelineIndicator = () => {
    if (!meta.isToday || ui.showEmptyState || ui.showUpgradeState) {
      return null;
    }
    const leftPxOffset = -10;

    return (
      <CurrentTimeIndicator
        pixelsPerMinute={pixelsPerMinute}
        leftPx={leftPxOffset}
        date={meta.date}
        fromHour={meta.fromDateTime}
        offsetTopPx={offsetTopPx}
      />
    );
  };

  const throttledVirtualScrollUpdate = (event: React.SyntheticEvent) => {
    virtualScrollbarRef.current?.scrollLeft?.(event?.currentTarget?.scrollLeft);
  };

  const handleScroll = useCallback(({ currentTarget }: React.MouseEvent<HTMLDivElement>) => {
    const { scrollLeft } = currentTarget;
    parentRef?.current?.scrollTo({ left: scrollLeft });
  }, []);

  const containerClass = classNames(styles.container, { [styles.compact]: compact });

  const addSpacingForTimestamps = (style: React.CSSProperties): React.CSSProperties => {
    const originalLeft = parseFloat(style.left?.toString() || '0');
    const left = originalLeft + totalTimestampsWidth + 'px';

    return {
      ...style,
      left,
    };
  };

  const backgroundStyle = generateStripedBgPattern({
    enabled: !ui.showEmptyState,
    height: pixelsPerMinute < 2 ? pixelsPerMinute * 30 : pixelsPerMinute * 15,
  });

  const rowVirtualizer = useVirtualizer({
    horizontal: true,
    count: timeline.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => MEMORY_WIDTH,
  });

  const timelineHourHeightPx = 60 * pixelsPerMinute;
  const timelineDayHeightPx = TIMELINE_PX_OFFSET + (meta.totalHours || 24) * timelineHourHeightPx;
  return (
    <React.Fragment>
      {isSubscriptionExpiredOrTrialEnded && <MemoryTimelineSubscriptionPrompt />}
      <TimelineZoomControl
        inline={inline}
        pixelsPerMinute={pixelsPerMinute}
        setPixelsPerMinute={setPixelsPerMinute}
      />
      <DisabledMask
        style={
          isSubscriptionExpiredOrTrialEnded
            ? {
                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 38.21%)',
              }
            : undefined
        }
        wrapperStyle={
          isSubscriptionExpiredOrTrialEnded
            ? {
                filter: 'initial',
              }
            : undefined
        }
        active={disabled}
      >
        <Scrollbars
          ref={scrollbarsRef}
          className={classNames({
            [styles.isSubscriptionExpiredOrTrialEnded]: isSubscriptionExpiredOrTrialEnded,
          })}
          variant={!ui.showEmptyState ? 'timeline' : undefined}
          data-testid="memory_timeline"
        >
          {ui.showPlaceholderMemories && <MemoryTimelinePlaceholder />}
          <div
            className={containerClass}
            style={{
              minHeight: timelineDayHeightPx,
              ...backgroundStyle,
              backgroundPositionY: offsetTopPx,
            }}
            id="timeline"
            draggable
            ref={timelineContainerRef}
          >
            <TimelineGutter
              snapToMinutes={snapToMinutes}
              timeFormat={timeFormat}
              timelineWidthPx={timelineWidthPx || 0}
              parentSelector="#timeline"
              parentRef={timelineContainerRef}
              pixelsPerMinute={pixelsPerMinute}
              hoursList={meta.hoursList}
              timelineHourHeightPx={timelineHourHeightPx}
              timelineDayHeightPx={timelineDayHeightPx}
              offsetTopPx={offsetTopPx}
              startOfDay={startOfDay}
              onSelect={handleTimestampChange}
              hideIndicators={ui.showEmptyState || ui.showUpgradeState}
            />

            <div
              ref={parentRef}
              onScroll={throttledVirtualScrollUpdate}
              className={styles.timelineLanes}
            >
              {renderTimelineIndicator()}
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                <div
                  ref={timespanRef}
                  style={{ position: 'relative', width: totalTimestampsWidth }}
                >
                  {renderHourTimespans()}
                </div>
                {rowVirtualizer.getVirtualItems().map(({ index, key, size, start }) => {
                  return (
                    <div
                      key={key}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: start + totalTimestampsWidth,
                        height: '100%',
                        width: `${size}px`,
                      }}
                    >
                      <div style={addSpacingForTimestamps({ left: start })}>
                        {renderMemories(timeline[index])}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </Scrollbars>
      </DisabledMask>
      {/*scrollbar gets hidden all the way at the bottom so we fake it to allow for manual click & drag*/}
      <Scrollbars
        style={{
          position: 'absolute',
          bottom: 2.5,
          left: 0,
          zIndex: 10,
        }}
        autoHeight
        width={MEMORY_WIDTH}
        onScroll={handleScroll}
        ref={virtualScrollbarRef}
      >
        <div
          style={{
            width: totalTimelineWidth,
            height: 15,
            background: 'transparent',
          }}
        />
      </Scrollbars>
      {ui.showEmptyState && !disabled && <MemoryTimelineEmptyState ui={ui} />}
    </React.Fragment>
  );
};
